import React, { useEffect, useState, useContext, useCallback } from "react";
import { Button, Collapse, Form } from 'react-bootstrap';
import { FaFilter, FaSort } from 'react-icons/fa';
import axios from "axios";
import "../App.css";
import Listing from "../components/Listing/listing";
import ListingModal from "../components/Modal/ListingModal";
import FilterPanel from '../components/FilterPanel/FilterPanel';
import { AppContext } from "../AppContext";
import { Link } from "react-router-dom";
// import { head } from "lodash";

export const Home = () => {
    const [posts, setPosts] = useState([]);
    const [savedPosts, setSavedPosts] = useState([]); // to keep track of saved posts
    const { port, token, isLoggedIn, userID, addAlert } = useContext(AppContext);
    const [selectedCity,] = useState("");
    const [selectedPost, setSelectedPost] = useState(null); // set the post to be displayed in the modal
    const [showModal, setShowModal] = useState(false);
    const [filters, setFilters] = useState({
        city: '',
        address: '',
        minPrice: '',
        maxPrice: '',
        startDate: '',
        endDate: '',
        preferences: '',
        dateMatchMode: 'flexible'
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [sortBy, setSortBy] = useState('');
    const [displayedPosts, setDisplayedPosts] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showGuide, setShowGuide] = useState(true);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (showGuide && isMobile) {
            const currentCount = parseInt(localStorage.getItem('visitCount') || '0');
            localStorage.setItem('visitCount', (currentCount + 1).toString());
        }
    }, [showGuide, isMobile]);

    useEffect(() => {
        let sorted = [...posts];
        switch(sortBy) {
            case 'price-asc':
                sorted.sort((a, b) => a.rent - b.rent); // if result is negative, a comes first
                break;
            case 'price-desc':
                sorted.sort((a, b) => b.rent - a.rent);
                break;
            default:
                // Keep original order
                sorted = [...posts];
        }
        setDisplayedPosts(sorted);        
    }, [posts, sortBy]);
    
    const fetchPosts = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        const queryParams = new URLSearchParams();
            
        if (filters.city) queryParams.append('city', filters.city);
        if (filters.minPrice) queryParams.append('minPrice', filters.minPrice);
        if (filters.maxPrice) queryParams.append('maxPrice', filters.maxPrice);
        if (filters.address) queryParams.append('address', filters.address);
        if (filters.startDate) queryParams.append('startDate', filters.startDate);
        if (filters.endDate) queryParams.append('endDate', filters.endDate);
        if (filters.preferences) queryParams.append('preferences', filters.preferences);
        if (filters.dateMatchMode) queryParams.append('dateMatchMode', filters.dateMatchMode);
        axios.get(`${port}/posts?${queryParams}`)
            .then((response) => {
                setPosts(Array.isArray(response.data) ? response.data : []);
            })
            .catch((error) => {
                console.error("Error fetching posts:", error);
                setError("Failed to fetch listings. Please try again later.");
                setPosts([]);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [filters.address, filters.city, filters.dateMatchMode, filters.endDate, filters.maxPrice, filters.minPrice, filters.preferences, filters.startDate, port]);    

    const fetchSavedPosts = useCallback(async () => { // to get all saved posts of a user
        if (!isLoggedIn) return;
        await axios.get(`${port}/posts/bookmarks/ids`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        })
            .then((response) => {
                setSavedPosts(response.data.bookmarks);
            })
            .catch((error) => {
                addAlert("Failed to fetch saved listings. Please try again later.", "danger");
                setSavedPosts([]);
            });
    }, [addAlert, isLoggedIn, port, token]);
    
    useEffect(() => { // everytime the city changes, fetch the posts
        fetchPosts();
        fetchSavedPosts();
    }, [fetchPosts, fetchSavedPosts, filters, isLoggedIn, port, selectedCity, token]);

    const savePost = async (post_id) => { // to save a post to the user's bookmarks
        if (isBookmarked(post_id)) {
            // Remove bookmark
            axios.delete(`${port}/posts/bookmarks`, {
                data: { userID, post_id },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })
                .then((response) => {
                    setSavedPosts(response.data.bookmarks);
                })
                .catch((error) => {
                    addAlert("Failed to remove bookmark. Please try again later.", "danger");
                });
        } else {
            // Add bookmark
            axios.patch(`${port}/posts/bookmark`, 
                { post_id },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            )
                .then((response) => {
                    setSavedPosts(response.data.bookmarks);
                })
                .catch((error) => {
                    addAlert("Failed to save listing. Please try again later.", "danger");
                });
        }
    };
    
    const isBookmarked = (post_id) => savedPosts && savedPosts.includes(post_id);

    // const handleCityChange = (event) => {
    //     setSelectedCity(event.target.value);
    // };

    const handleListingClick = (post) => { // show modal when a listing is clicked
        setSelectedPost(post);
        setShowModal(true);
    };

    const handleResetFilters = () => {
        setFilters({
            city: '',
            address: '',
            minPrice: '',
            maxPrice: '',
            startDate: '',
            endDate: '',
            preferences: '',
            dateMatchMode: 'flexible'
        });
    };

    return (
        <div className="container py-4">
            {isMobile && showGuide && (
                <div className="alert alert-info alert-dismissible fade show mb-4" role="alert">
                    <i className="fas fa-info-circle me-2"></i>
                    {isLoggedIn ? (
                        <>To create a new listing, click the menu button (top right) <i className="fas fa-bars"></i> and select "New Post"</>
                    ) : (
                        <>To create a listing, click the menu button (top right) <i className="fas fa-bars"></i> to log in or register first</>
                    )}
                    <button type="button" className="btn-close" onClick={() => setShowGuide(false)}></button>
                </div>
            )}

            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>Listings</h1>
                <div className="d-flex gap-3">
                    {/* {isLoggedIn && (
                        <Link 
                            to="/new-post" 
                            className="btn btn-primary d-flex align-items-center gap-2"
                        >
                            <i className="fas fa-plus"></i>
                            New Listing
                        </Link>
                    )} */}
                    <div className="d-flex align-items-center">
                        <FaSort className="me-2 text-muted" />
                        <Form.Select 
                            size="sm" 
                            style={{ width: 'auto' }}
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <option value="">Sort By</option>
                            <option value="price-asc">Price: Low to High</option>
                            <option value="price-desc">Price: High to Low</option>
                        </Form.Select>
                    </div>
                    
                    <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => setShowFilters(!showFilters)}
                        className="d-flex align-items-center gap-2"
                    >
                        <FaFilter />
                        {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                </div>
            </div>

            {posts.length > 0 && (
                <p className="text-muted mb-3">
                    Showing {posts.length} listing{posts.length !== 1 ? 's' : ''}
                </p>
            )}

            <Collapse in={showFilters}>
                <div>
                    <FilterPanel 
                        filters={filters}
                        setFilters={setFilters}
                        onReset={handleResetFilters}
                    />
                </div>
            </Collapse>
            
            {isLoading ? (
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : (
                <div className="row">
                    {Array.isArray(displayedPosts) && displayedPosts.length > 0 ? (
                        displayedPosts.map((post) => (
                            <div className="col-md-4 mb-4" key={post._id}>
                                <div className="listing-wrapper">
                                    <Listing 
                                        post={post}
                                        onBookmark={savePost}
                                        isBookmarked={isBookmarked(post._id)}
                                        onClickListing={handleListingClick}
                                    />
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="text-center">No listings available at the moment.</p>
                    )}
                </div>
            )}
            
            {error && (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            )}

            {/* Modal is conditionally rendered only when a post is selected */}
            {selectedPost && ( 
                <ListingModal 
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    post={selectedPost}
                />
            )}
        </div>
    );
};